import React, { useState, useEffect } from 'react';
import { X, Search, UserPlus } from 'lucide-react';

const AssignMembersModal = ({
    isOpen,
    onClose,
    onAssign,
    organizationId,
    token,
    snagIds = []
}) => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [assigning, setAssigning] = useState(false);

    const API_BASE_URL = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");

    useEffect(() => {
        if (isOpen && organizationId && token) {
            fetchMembers();
            setSelectedMembers([]);
            setSearch('');
        }
    }, [isOpen, organizationId, token]);

    const fetchMembers = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/org/organization/${organizationId}/members/`, {
                method: "GET",
                headers: { Authorization: `Bearer ${token}` }
            });
            const payload = await res.json();
            if (res.ok) {
                const dataList = Array.isArray(payload) ? payload : (payload.data || []);
                setMembers(dataList);
            }
        } catch (e) {
            console.error("Failed to fetch members", e);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleMember = (id) => {
        setSelectedMembers(prev => 
            prev.includes(id) ? prev.filter(mId => mId !== id) : [...prev, id]
        );
    };

    const handleAssign = async () => {
        if (selectedMembers.length === 0 || snagIds.length === 0) return;
        setAssigning(true);
        
        try {
            const res = await fetch(`${API_BASE_URL}/snag-management/org/${organizationId}/snags/assign/multiple/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    snags: snagIds,
                    member_ids: selectedMembers
                })
            });
            
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || err.detail || "Failed to assign members");
            }
            
            onAssign();
            onClose();
        } catch (e) {
            console.error(e);
            alert(e.message);
        } finally {
            setAssigning(false);
        }
    };

    const filteredMembers = members.filter(m => {
        const name = `${m.user?.first_name || ''} ${m.user?.last_name || ''}`.toLowerCase();
        const email = (m.user?.email || '').toLowerCase();
        const searchLower = search.toLowerCase();
        return name.includes(searchLower) || email.includes(searchLower);
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[80vh]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900">Assign Members</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                
                <div className="p-4 border-b border-gray-100">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search members..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sm-primary/20 focus:border-sm-primary"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-2">
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <span className="text-sm text-gray-500">Loading members...</span>
                        </div>
                    ) : filteredMembers.length === 0 ? (
                        <div className="flex justify-center p-8">
                            <span className="text-sm text-gray-500">No members found.</span>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {filteredMembers.map(member => (
                                <label 
                                    key={member.id} 
                                    className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                                >
                                    <div className="flex items-center h-5">
                                        <input
                                            type="checkbox"
                                            checked={selectedMembers.includes(member.id)}
                                            onChange={() => handleToggleMember(member.id)}
                                            className="w-4 h-4 text-sm-primary border-gray-300 rounded focus:ring-sm-primary"
                                        />
                                    </div>
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-sm-primary/10 text-sm-primary font-medium text-xs">
                                        {(member.user?.first_name?.[0] || '') + (member.user?.last_name?.[0] || '')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {member.user?.first_name} {member.user?.last_name}
                                        </p>
                                        <p className="text-xs text-gray-500 truncate">
                                            {member.joined_as || member.user?.profession || 'Member'}
                                        </p>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleAssign}
                        disabled={assigning || selectedMembers.length === 0}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-sm-primary rounded-lg hover:bg-sm-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <UserPlus className="w-4 h-4" />
                        {assigning ? 'Assigning...' : `Assign (${selectedMembers.length})`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignMembersModal;
